import { useQuery } from "@tanstack/react-query";
import { GET_MY_PRODUCTS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import { hasAuthToken } from "../utils/tokenUtils";
import type { MyProduct } from "../types/products";

export type { MyProduct };

interface GetMyProductsResponse {
  getMyProducts: MyProduct[];
}

/**
 * Loads the products the signed-in user is entitled to (derived server-side
 * from their enrollments' `productCodes`), each enriched with the product's
 * learner/educator plan codes. Distinct from `useProducts`, which loads the
 * full published product catalog from S3.
 */
export const useMyProducts = () => {
  const { data, error, isLoading, refetch } = useQuery<MyProduct[]>({
    queryKey: ["myProducts"],
    queryFn: async () => {
      const resp =
        await graphQLClient.request<GetMyProductsResponse>(GET_MY_PRODUCTS);
      return resp.getMyProducts ?? [];
    },
    // Nothing to resolve without credentials — keeps public screens (login,
    // maintenance takeover) from firing an unauthenticated request.
    enabled: hasAuthToken(),
  });

  useReactQueryErrorReporter("Could not load your products", error, false);

  return { products: data ?? [], loading: isLoading, error, refetch };
};
